import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private urlApi = environment.url_api;

  httpOptions: any = {
    headers: new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    }),
  };

  /** Subject to listen changes in full name user */
  private fullNameSubject$ = new BehaviorSubject<string>('');
  /** Subject to listen changes in avatar user */
  private avatarUserSubject$ = new BehaviorSubject<string>('');

  constructor(private http: HttpClient) {
    // Initialize with current user data if available
    this.refreshUserData();
  }

  /**
   * Refresh user data from localStorage
   * This method should be called after login/logout to update the subjects
   */
  refreshUserData(): void {
    try {
      const dataUser = localStorage.getItem('dataUser');
      const avatarImg = localStorage.getItem('imgUser');

      if (dataUser) {
        const parsedData = JSON.parse(dataUser);
        this.fullNameSubject$.next(parsedData?.full_name ?? '');
      } else {
        this.fullNameSubject$.next('');
      }

      this.avatarUserSubject$.next(avatarImg ?? 'assets/img/avatar-default.svg');
    } catch (error) {
      console.error('Error refreshing user data:', error);
      this.fullNameSubject$.next('');
      this.avatarUserSubject$.next('assets/img/avatar-default.svg');
    }
  }

  /**
   * Clear user data (used during logout)
   */
  clearUserData(): void {
    this.fullNameSubject$.next('');
    this.avatarUserSubject$.next('assets/img/avatar-default.svg');
  }

  /**
   * Get all users
   **/
  getAllUsers(page: number, company: string | undefined, search: string, filterKey = 'first_name'): Observable<any> {
    let URI: string = this.urlApi + 'users/criteria?page=' + page + '&limit=10';

    if (company !== '' && company !== undefined && search !== '') {
      URI = URI + '&filters={"company":{"=":"' + company + '"},"' + filterKey + '":{"regex":"' + search + '"}}';
    }

    if (company !== '' && company !== undefined && search === '') {
      URI = URI + '&filters={"company":{"=":"' + company + '"}}';
    }

    if ((company === '' || company === undefined) && search !== '') {
      URI = URI + '&filters={"' + filterKey + '":{"regex":"' + search + '"}}';
    }

    return this.http.get(URI, this.httpOptions);
  }

  /**
   * Get all companies
   **/
  getAllCompanies(): Observable<any> {
    const URI: string = this.urlApi + 'company';

    return this.http.get(URI, this.httpOptions);
  }

  getUserById(id: string): Observable<any> {
    const URI: string = this.urlApi + `user/${id}`;

    return this.http.get(URI, this.httpOptions);
  }

  updateProfileUser(id: string, data: any): Observable<any> {
    const dataUser = localStorage.getItem('dataUser');
    const userId = id ?? (dataUser ? JSON.parse(dataUser)?.user_id : null);
    const URI: string = this.urlApi + `user/${userId}`;

    return this.http.put(URI, data);
  }

  updateUserInfo(id: string, data: any): Observable<any> {
    const dataUser = localStorage.getItem('dataUser');
    const userId = id ?? (dataUser ? JSON.parse(dataUser)?.user_id : null);
    const URI: string = this.urlApi + `user/update-client-dashboard/${userId}`;

    return this.http.patch(URI, data);
  }

  updateOnboardingQuestions(id: string, data: any): Observable<any> {
    const dataUser = localStorage.getItem('dataUser');
    const userId = id ?? (dataUser ? JSON.parse(dataUser)?.user_id : null);
    const URI: string = this.urlApi + `user/update-user-onboarding-client-dashboard/${userId}`;

    return this.http.patch(URI, data);
  }

  getOnboardingQuestions(id: string): Observable<any> {
    const URI: string = this.urlApi + `user/onboarding-client-dashboard/${id}`;

    return this.http.get(URI);
  }

  changePassword(data: any): Observable<any> {
    const dataUser = localStorage.getItem('dataUser');
    const userId = dataUser ? JSON.parse(dataUser)?.user_id : null;
    const URI: string = this.urlApi + `user/change-password/${userId}`;

    return this.http.put(URI, data);
  }

  updatePhotoUser(userId: string, dataUser: any): Observable<any> {
    const URI = this.urlApi + `user/avatar/${userId}`;

    return this.http.put(URI, dataUser);
  }

  setLanguage(id: string, lang: string): Observable<any> {
    const uri = this.urlApi + 'user/' + id + '/update-language';

    return this.http.put(uri, { lang });
  }

  /**
   * Update user data from external source (e.g., after profile update)
   * @param {string} fullName - User's full name
   * @param {string} avatar - User's avatar URL
   */
  updateUserData(fullName: string, avatar?: string): void {
    this.fullNameSubject$.next(fullName);

    if (avatar) {
      this.avatarUserSubject$.next(avatar);
      localStorage.setItem('imgUser', avatar);
    }

    // Update localStorage dataUser if available
    try {
      const currentDataSession = localStorage.getItem('dataUser');
      if (currentDataSession) {
        const parsedData = JSON.parse(currentDataSession);
        localStorage.setItem(
          'dataUser',
          JSON.stringify({
            ...parsedData,
            full_name: fullName,
          })
        );
      }
    } catch (error) {
      console.error('Error updating localStorage dataUser:', error);
    }
  }

  /**
   * Get full name user
   */
  get userFullName(): Observable<string> {
    return this.fullNameSubject$.asObservable();
  }
  /**
   * Get avatar user
   */
  get userAvatar(): Observable<string> {
    return this.avatarUserSubject$.asObservable();
  }
  /**
   * change full name user
   * @param {string} fullName
   */
  changeFullNameUser(fullName: string): void {
    this.fullNameSubject$.next(fullName);
    const currentDataSession = JSON.parse(localStorage.getItem('dataUser') ?? '');
    localStorage.setItem(
      'dataUser',
      JSON.stringify({
        ...currentDataSession,
        full_name: fullName,
      })
    );
    // Refresh user data to ensure consistency
    this.refreshUserData();
  }
  /**
   * change avatar user
   * @param {string} avatar
   */
  changeAvatarUser(avatar: string): void {
    this.avatarUserSubject$.next(avatar);
    localStorage.setItem('imgUser', avatar);
    // Refresh user data to ensure consistency
    this.refreshUserData();
  }
  /**
   * unsubscribe all observables
   */
  unsubscribeObservables(): void {
    this.fullNameSubject$.complete();
    this.avatarUserSubject$.complete();
  }

  getUserInformation(): Observable<any> {
    const URI = this.urlApi + 'user-entitlements/dashboard';

    return this.http.get(URI, this.httpOptions);
  }
}
